import type { PublicOptions, WeekAvailability } from '@pa/api/public'
import { Avatar, fmtTime, SlowNotice, sundayOf, TimeSlots, WeekCalendar } from '@pa/ui'
import { AlertTriangle, ArrowLeft, ArrowRight, CalendarClock, Phone } from 'lucide-react'
import { ScreenTitle } from '@/features/entry'
import type { Banner, DateTimePick, Slot } from '../model/machine'
import type { Choice } from '../model/resolve'

/** "Primary Care Center – Downtown" → "Downtown" (the mockup's `shortLocation`). */
export const shortLocation = (name: string): string => name.split(' – ').pop() ?? name

/** The P-08 banners, verbatim from UserFlows P-08 (title from the mockup's `conflict-banner`). */
export const BANNERS: Record<Banner, { title: string; text: string }> = {
  taken: {
    title: 'That time was just taken',
    text: 'Another patient reserved it a moment ago. Please choose a different time.',
  },
  holdRanOut: {
    title: 'That time is no longer available',
    text: 'Your 5-minute hold ran out, so the time was released. Please pick a time again.',
  },
  expiredAtConfirm: {
    title: 'That time is no longer available',
    text: 'Your 5-minute hold ran out before we could confirm, so the time was released. Please pick a time again.',
  },
  takenAtConfirm: {
    title: 'That time is no longer available',
    text: 'Sorry, that time was just booked by someone else. Please choose another time.',
  },
}

/**
 * P-08 "When works best for you?" (UserFlows P-08), ported from the mockup's `patient/steps.tsx` `DateTimeStep`:
 * month tabs and a Sunday-start week, the time list for the picked day, the "{time} with {provider}" summary, and
 * Continue → "Holding your time…". No day is preselected. Task 07: the banner that says why the patient is back
 * here, struck-through "just taken" times, the loading state of a week ("Finding open times…", shimmering days,
 * `aria-busy`) and the slow notices (week and hold).
 */
export function DateTimeStep({
  stepLabel,
  options,
  choice,
  pick,
  week,
  loading,
  holding,
  banner = null,
  timeZone,
  phone,
  onWeek,
  onPickDay,
  onPickSlot,
  onBack,
  onContinue,
}: {
  stepLabel: string
  options: PublicOptions
  choice: Choice
  pick: DateTimePick
  /** The shown week; `undefined` while loading. */
  week: WeekAvailability | undefined
  /** The week is loading (first visit, another week, a month tab; never a 20 s poll). */
  loading: boolean
  holding: boolean
  banner?: Banner | null
  timeZone: string
  phone: string
  onWeek: (weekStart: string) => void
  onPickDay: (day: string) => void
  onPickSlot: (slot: Slot) => void
  onBack: (() => void) | undefined
  onContinue: () => void
}) {
  const window = week?.window ?? options.window
  const weekStart = pick.weekStart ?? sundayOf(window.firstDate)
  const days = week?.weekStart === weekStart ? week.days : undefined
  const daySlots = days?.find((d) => d.date === pick.day)?.slots ?? []
  const provider = options.providers.find((p) => p.id === choice.providerId)
  const location = options.locations.find((l) => l.id === choice.locationId)
  const tele = options.visitTypes.find((t) => t.id === choice.visitTypeId)?.telehealth === true
  const lastWeek = week?.nextWeekStart === null
  const shownBanner = banner ? BANNERS[banner] : null

  return (
    <>
      <div>
        <div className="pt-step-label">{stepLabel}</div>
        <ScreenTitle className="pt-title">When works best for you?</ScreenTitle>
      </div>
      <div className="pt-body">
        {shownBanner && (
          <div className="conflict-banner" role="alert">
            <AlertTriangle aria-hidden />
            <div>
              <b>{shownBanner.title}</b>
              {shownBanner.text}
            </div>
          </div>
        )}
        <div className="dt">
          <WeekCalendar
            weekStart={weekStart}
            days={days?.map((d) => ({ ...d, slotCount: d.slots.length }))}
            window={window}
            today={window.firstDate}
            value={pick.day}
            canPrev={week ? week.prevWeekStart !== null : false}
            canNext={week ? week.nextWeekStart !== null : false}
            onWeekChange={onWeek}
            onPick={onPickDay}
            emptyWeek={(hasNext) => (
              <div className="wk-empty">
                <CalendarClock aria-hidden />
                <b>No openings this week</b>
                <span>
                  {hasNext && !lastWeek
                    ? 'Try the next week, or call us and we’ll find you a time.'
                    : 'Please call us and we’ll find you a time.'}
                </span>
                {phone !== '' && (
                  <a className="btn btn-secondary btn-sm" href={`tel:${phone.replace(/\D/g, '')}`}>
                    <Phone aria-hidden /> {phone}
                  </a>
                )}
              </div>
            )}
          />
          <div className="dt-slots" aria-busy={loading || undefined}>
            <TimeSlots
              date={days ? pick.day : null}
              slots={daySlots}
              timeZone={timeZone}
              selected={pick.slot?.start ?? null}
              onSelect={onPickSlot}
              taken={pick.taken}
              loading={loading}
            />
            <SlowNotice key={weekStart} pending={loading} />
            {pick.slot && provider && (
              <div className="row slot-summary">
                <Avatar name={provider.name} color={provider.color} size="sm" />
                <div className="grow slot-pick">
                  <b>
                    {fmtTime(pick.slot.start, timeZone)} with {provider.name}
                  </b>
                  <div className="muted">
                    {tele ? 'Video visit' : `${location ? shortLocation(location.name) : ''} office`}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="pt-actions">
        {onBack && (
          <button type="button" className="back-link" aria-label="Back" onClick={onBack}>
            <ArrowLeft aria-hidden /> <span className="bl-text">Back</span>
          </button>
        )}
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-primary btn-lg"
          disabled={pick.slot === null || holding}
          aria-busy={holding}
          onClick={onContinue}
        >
          {holding ? (
            <>
              <span className="spinner" aria-hidden /> Holding your time…
            </>
          ) : (
            <>
              Continue <ArrowRight aria-hidden />
            </>
          )}
        </button>
      </div>
      <SlowNotice pending={holding} />
    </>
  )
}
